// Sliding Window — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as twoPointers.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import slidingWindowDrills from './slidingWindowDrills';
const drill = id => slidingWindowDrills.find(d => d.id === id);

const slidingWindow = {
  key: 'Sliding Window',
  tagline: 'Keep a contiguous range alive across the array, updating it in O(1) per step instead of recomputing it from scratch.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Sliding Window',
      type: 'text',
      body: [
        "A sliding window is a contiguous range [left, right] inside an array or string that moves across the data one step at a time, instead of being thrown away and rebuilt for every new position.",
        "It's really a specialized same-direction two-pointer technique — but where two pointers usually track two single positions, a sliding window treats the space BETWEEN two pointers as the thing being maintained: its sum, its character counts, its length. The pointers move; the window's running state updates incrementally along with them.",
      ],
    },
    {
      id: 'why',
      title: 'Why Sliding Window?',
      type: 'text',
      body: [
        "Any problem shaped like \"find something about every contiguous subarray/substring of size k\" or \"find the best contiguous range satisfying some condition\" has an obvious brute-force shape: examine every range, from scratch, and compare. For a fixed size k, that's O(n · k). For a variable size, naive brute force is O(n²) or worse.",
        "The waste is always the same: adjacent windows share almost all of their elements. Moving from window [i, i+k-1] to [i+1, i+k] only changes two things — one element leaves on the left, one enters on the right. Recomputing the whole window from scratch throws away everything the previous window already knew.",
        "Sliding window fixes this by keeping running state (a sum, a frequency map, a counter) and updating it incrementally: subtract what left, add what entered. That turns an O(k) or O(n) per-window cost into O(1) per step, collapsing the total work to O(n).",
      ],
    },
    {
      id: 'analogy',
      title: 'How Sliding Window Works — Two Real-World Pictures',
      type: 'text',
      heading: 'A Camera Panning a Mural, and a Rubber Band Around a Handful of Coins',
      body: [
        "Picture a museum guide holding up a fixed-size picture frame and sliding it along a long mural, always covering exactly the same width. To describe what's newly visible after each slide, the guide doesn't re-describe the whole frame from zero — they just note \"this panel left view on the left, this new panel entered on the right,\" and adjust their running description accordingly. That's a fixed-size window: left and right always stay exactly k apart, and each slide is a one-in, one-out update.",
        "Now picture a rubber band stretched around a handful of coins on a table, and the goal is to capture coins totaling at least a certain value. You keep adding coins to the band (stretching it — expanding right) until the total is enough. Then, since the band is elastic, you try shrinking it from the other end (pulling left inward) to see how few coins you can get away with while still meeting the total — stopping the instant it would drop below target. That's a variable-size window: it grows when it needs more, and shrinks when it can afford to give some back, always searching for the tightest range that still satisfies the condition.",
      ],
    },
    {
      id: 'internals',
      title: 'Visualizing a Window',
      type: 'window-diagram',
      body: [
        "A window is just two indices, left and right, with everything between them (inclusive) treated as \"currently active.\" The diagram below freezes one moment of a window mid-slide — this is exactly what a debugger would show if you paused execution inside the loop.",
        "Whatever running state the window is tracking (a sum, a character frequency map, a zero counter) only reflects the elements currently highlighted. The moment left or right moves, that state gets updated incrementally — never recomputed from the highlighted range's raw values.",
      ],
      demo: {
        values: [4, 2, 1, 7, 8, 1, 2],
        left: 2,
        right: 4,
        label: 'window [2, 4] = {1, 7, 8} — sum = 16. If right moves to 5, subtract nothing and add arr[5]=1 → sum becomes 17. If left then moves to 3, subtract arr[2]=1 → sum becomes 16 again.',
      },
    },
    {
      id: 'types',
      title: 'Fixed-Size vs. Variable-Size Windows',
      type: 'text',
      body: [
        "Fixed-size window: the window's length k is given up front and never changes. left and right always stay exactly k − 1 apart. Every slide is symmetric — exactly one element leaves, exactly one enters. Used for \"best window of size k\" problems (maximum sum, minimum, average, first-negative-per-window).",
        "Variable-size window: the window grows and shrinks based on a condition, not a fixed length. right always expands forward; left only moves when the current window violates (or, depending on the problem, satisfies) some rule. Used for \"smallest/longest window meeting a condition\" problems — sum ≥ target, at most k distinct characters, at most k zeros flippable.",
        "The tell for which variant a problem wants: if the problem names an exact size k, it's fixed. If it asks for the \"longest\" or \"shortest\" window satisfying some property, it's variable.",
      ],
    },
    {
      id: 'declare',
      title: 'Setting Up a Window in Java',
      type: 'code',
      code:
`// Fixed-size: right always trails left by exactly k - 1
int left = 0;
for (int right = 0; right < arr.length; right++) {
    // ... add arr[right] to running state ...
    if (right >= k - 1) {
        // window is full-size — read/record the answer here
        // ... then remove arr[left] from running state, left++ ...
    }
}

// Variable-size: left only moves in response to a condition
int left = 0;
for (int right = 0; right < arr.length; right++) {
    // ... add arr[right] to running state ...
    while (/* window violates some condition */) {
        // ... remove arr[left] from running state, left++ ...
    }
}`,
      note: 'Both shapes share the same skeleton: right always drives the loop, forward, one step at a time. The only difference is what triggers left to move — a fixed size, or a while condition.',
    },
    {
      id: 'fixedtemplate',
      title: 'Fixed-Size Window Template',
      type: 'code',
      code:
`public int maxSumSubarray(int[] arr, int k) {
    int sum = 0;
    for (int i = 0; i < k; i++) sum += arr[i];   // first window, built once
    int max = sum;

    for (int right = k; right < arr.length; right++) {
        int left = right - k;
        sum = sum - arr[left] + arr[right];      // O(1) slide: one out, one in
        max = Math.max(max, sum);
    }
    return max;
}`,
      note: 'The first window is the only one ever built from scratch. Every window after that is a single subtract-then-add — the entire reason this pattern beats the brute-force O(n · k) rescan.',
    },
    {
      id: 'variabletemplate',
      title: 'Variable-Size Window Template',
      type: 'code',
      code:
`public int smallestSubarray(int[] arr, int target) {
    int left = 0, sum = 0, minLen = Integer.MAX_VALUE;

    for (int right = 0; right < arr.length; right++) {
        sum += arr[right];                       // always expand right
        while (sum >= target) {                   // shrink while the condition still holds
            minLen = Math.min(minLen, right - left + 1);
            sum -= arr[left];
            left++;
        }
    }
    return minLen == Integer.MAX_VALUE ? 0 : minLen;
}`,
      note: 'right never moves backward and left never moves backward either — each index is added to the window exactly once and removed at most once, which is what keeps this O(n) instead of O(n²) despite the nested-looking while loop.',
    },
    {
      id: 'freqwindow',
      title: 'Combining a Window With a Frequency Map',
      type: 'code',
      code:
`public int longestSubstringKDistinct(String s, int k) {
    Map<Character, Integer> freq = new HashMap<>();
    int left = 0, maxLen = 0;

    for (int right = 0; right < s.length(); right++) {
        char c = s.charAt(right);
        freq.put(c, freq.getOrDefault(c, 0) + 1);   // expand: add to the window's frequency map

        while (freq.size() > k) {                    // shrink while too many distinct characters
            char lc = s.charAt(left);
            freq.put(lc, freq.get(lc) - 1);
            if (freq.get(lc) == 0) freq.remove(lc);   // fully removed — no longer "in" the window
            left++;
        }
        maxLen = Math.max(maxLen, right - left + 1);
    }
    return maxLen;
}`,
      note: 'This is where Sliding Window and Hashing meet: the window\'s "running state" doesn\'t have to be a single number — it can be an entire HashMap, updated incrementally the same way a running sum is.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Rebuilding the window\'s state from scratch on every slide',
          why: 'Recomputing a sum or a frequency map by rescanning the whole current window on every step throws away the entire point of the pattern — it silently degrades an intended O(n) solution back to O(n · k) or worse.',
          fix: 'Always update state incrementally: subtract/decrement what left, add/increment what entered. If you find yourself looping over the window\'s contents inside the outer loop, that\'s a sign the update isn\'t truly O(1).',
        },
        {
          mistake: 'Using an "if" instead of a "while" to shrink a variable window',
          why: 'A single if only shrinks by one element even when the window needs to shrink by several — the window can be left in an invalid state that the rest of the algorithm assumes never happens.',
          fix: 'Shrinking is a while loop: keep removing from the left until the window is valid again, not just once.',
        },
        {
          mistake: 'Forgetting to fully remove a key from a frequency map when its count hits zero',
          why: 'If a character\'s count drops to 0 but the key is left in the map, freq.size() still counts it as "present" — a distinct-character check like freq.size() > k will be wrong even though the count is technically zero.',
          fix: 'When a count reaches zero after removing an outgoing element, delete the key entirely — don\'t just leave a zero behind.',
        },
        {
          mistake: 'Off-by-one on the fixed-window boundary condition',
          why: 'Checking if (right >= k) instead of if (right >= k - 1) either skips recording the very first valid window or starts recording one slide too early.',
          fix: 'The window first becomes full-size when right == k - 1 (0-indexed) — that\'s the first moment worth reading the answer from.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Fixed-size window slide (per step)', time: 'O(1)', note: 'Exactly one element leaves, one enters — a constant amount of work regardless of k.' },
        { op: 'Fixed-size window, full scan', time: 'O(n)', note: 'One O(1) slide per position, n − k + 1 windows total.' },
        { op: 'Variable-size window, full scan', time: 'O(n)', note: 'Amortized: right advances n times total; left also advances at most n times total across the whole run, never resetting backward.' },
        { op: 'Brute-force rescan (what sliding window replaces)', time: 'O(n · k) or O(n²)', note: 'Recomputing every window\'s state from its raw elements, instead of reusing the previous window\'s state.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for sliding window when you see…',
      body: [
        'A request about every contiguous subarray or substring of a FIXED size k (max/min/average per window).',
        'A request for the LONGEST or SHORTEST contiguous range satisfying some condition (a sum threshold, a distinct-character limit, a flip budget).',
        'A brute-force instinct that would recompute a sum, count, or frequency map from scratch for every window — that recomputation is almost always avoidable with an incremental update.',
        'Avoid it when the best subrange doesn\'t have to be contiguous (that\'s usually a different pattern — often dynamic programming), or when growing the window doesn\'t change its validity in one predictable direction (sliding window relies on that monotonic behavior, the same way opposite-direction two pointers rely on sortedness).',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('max-sum-subarray-k'),
            drill('average-subarrays-k'),
            drill('first-negative-in-window'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('smallest-subarray-sum-target'),
            drill('longest-substring-k-distinct'),
            drill('max-consecutive-ones-k-flips'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('longest-subarray-sum-exactly-k'),
            drill('count-anagram-substrings'),
            drill('subarray-product-less-than-k'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with both fixed and variable windows before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Fixed-size window: left and right always stay exactly k − 1 apart. Every slide removes one element and adds one — O(1) per step.',
        'Variable-size window: right always expands; left only moves in response to a while-condition, never resetting backward.',
        'The running state can be anything — a sum, a product, a counter, or a full frequency HashMap — as long as it updates incrementally instead of being rebuilt.',
        'Shrinking is always a while loop, never a single if — the window might need to give back more than one element to become valid again.',
        'When a frequency map entry\'s count hits zero, delete the key — a lingering zero entry silently breaks freq.size() checks.',
        'The signal to use this pattern: "every window of size k," or "longest/shortest contiguous range satisfying X."',
        'If growing the window doesn\'t move validity in one predictable direction, sliding window is the wrong tool — that monotonic behavior is the pattern\'s core assumption.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default slidingWindow;
